import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Rocket, CheckCircle2, XCircle, Clock, Sparkles, FileText, Search, Calendar, Brain, Wrench, ArrowRight, AlertTriangle, TrendingUp, Zap, Plug } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { toast } from "sonner";

const STEPS = [
  { key: "business_analysis", label: "Business Analysis", icon: Sparkles, hint: "Profiling your business + competitors" },
  { key: "seo_audit", label: "SEO Audit", icon: Search, hint: "Scoring + finding issues" },
  { key: "content_plan", label: "30-Day Content Plan", icon: Calendar, hint: "Mapping a month of articles" },
  { key: "auto_blogs", label: "Auto-write blog posts", icon: FileText, hint: "Drafting articles + cover images" },
  { key: "llm_queries", label: "LLM Visibility queries", icon: Brain, hint: "Tracking AI search mentions" },
  { key: "fix_kit", label: "Auto-Fix Kit", icon: Wrench, hint: "Paste-able patches per issue" },
];

export default function AutopilotPage() {
  const navigate = useNavigate();
  const { active, projects } = useProjects();
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const [blogCount, setBlogCount] = useState(5);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  // Find latest job for active project
  const fetchLatest = async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/autopilot/jobs?project_id=${active.id}`);
      if (data?.length) setJob(data[0]);
    } catch {}
  };

  useEffect(() => { fetchLatest(); }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll while running
  useEffect(() => {
    if (!job?.id || (job.status !== "running" && job.status !== "queued")) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/autopilot/jobs/${job.id}`);
        setJob(data);
      } catch {}
    }, 2500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [job?.id, job?.status]);

  const start = async () => {
    if (!active) { setError("Activate a project first"); return; }
    setStarting(true); setError("");
    try {
      const { data } = await api.post("/autopilot/start", { project_id: active.id, blog_count: blogCount });
      setJob(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setStarting(false); }
  };

  if (!active) {
    return (
      <DashboardLayout eyebrow="Autopilot" title="One-click growth engine." subtitle="Link a domain, then we run the entire SEO + content + AI-search pipeline automatically.">
        <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center" data-testid="autopilot-no-project">
          <Rocket className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <h3 className="font-display text-xl mb-2">No project selected</h3>
          <p className="text-zinc-400 mb-6">Add a domain first — then autopilot can take over.</p>
          <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="autopilot-onboarding-cta">
            <Link to="/onboarding">Add your first project <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const isRunning = job?.status === "running" || job?.status === "queued";
  const isDone = job?.status === "completed";
  const failed = job?.status === "failed";

  return (
    <DashboardLayout
      eyebrow="Autopilot"
      title="One-click growth engine."
      subtitle="Link a domain, then we run the entire SEO + content + AI-search pipeline automatically."
    >
      {/* Hero / control */}
      <div className="bg-gradient-to-br from-[#0055FF]/10 via-zinc-950 to-zinc-950 border border-[#0055FF]/30 rounded-sm p-8 mb-6 relative overflow-hidden" data-testid="autopilot-control">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#0055FF]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="relative grid md:grid-cols-[1fr_auto] gap-6 items-end">
          <div>
            <span className="label-eyebrow">Active project</span>
            <h2 className="font-display text-3xl font-medium mt-1">{active.name} <span className="text-zinc-500 text-base ml-2">{active.domain}</span></h2>
            <p className="text-zinc-400 mt-2 max-w-xl leading-relaxed">
              When you click Run, GPT-5.2 + Nano Banana will execute six steps end-to-end. Takes ~3-5 minutes.
            </p>
            {error && <div className="mt-3 text-sm text-red-400" data-testid="autopilot-error">{error}</div>}
          </div>
          <div className="flex flex-col gap-3 min-w-[220px]">
            <div>
              <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Blogs to draft</Label>
              <Select value={String(blogCount)} onValueChange={(v) => setBlogCount(parseInt(v))}>
                <SelectTrigger className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="autopilot-blog-count"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                  <SelectItem value="3">3 posts</SelectItem>
                  <SelectItem value="5">5 posts</SelectItem>
                  <SelectItem value="8">8 posts</SelectItem>
                  <SelectItem value="10">10 posts</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={start} disabled={starting || isRunning}
                    className="h-12 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm font-medium glow-beam"
                    data-testid="autopilot-start-btn">
              {starting || isRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Rocket className="w-4 h-4 mr-2" />}
              {isRunning ? "Running…" : isDone ? "Run again" : "Run autopilot"}
            </Button>
          </div>
        </div>
      </div>

      {/* GSC Money Loop section */}
      <GscMoneyLoop />

      {/* Progress / steps */}
      {job && (
        <div className="space-y-3" data-testid="autopilot-steps">
          {STEPS.map((s) => {
            const stepData = job.steps?.[s.key] || { status: "pending" };
            return (
              <StepRow key={s.key} step={s} data={stepData} jobBlogCount={job.blog_count} />
            );
          })}
        </div>
      )}

      {/* Outcome cards */}
      {isDone && (
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4 fade-in" data-testid="autopilot-outcomes">
          {job.steps?.business_analysis?.status === "done" && (
            <OutcomeCard title="Business analyzed" hint={job.steps.business_analysis.summary} cta="View analysis" to="/onboarding" testid="outcome-analysis" />
          )}
          {job.steps?.seo_audit?.status === "done" && (
            <OutcomeCard title={`SEO score: ${job.steps.seo_audit.score ?? "—"}`} hint="Issues + recommendations ready" cta="Open audit" to="/audit" testid="outcome-audit" />
          )}
          {job.steps?.content_plan?.status === "done" && (
            <OutcomeCard title={`${job.steps.content_plan.post_count} posts planned`} hint="30-day calendar" cta="View calendar" to="/content-plan" testid="outcome-plan" />
          )}
          {job.steps?.auto_blogs?.status === "done" && (
            <OutcomeCard title={`${job.steps.auto_blogs.drafted}/${job.steps.auto_blogs.target} blogs drafted`} hint="With cover images" cta="View drafts" to="/blog" testid="outcome-blogs" />
          )}
          {job.steps?.llm_queries?.status === "done" && (
            <OutcomeCard title={`${job.steps.llm_queries.count} LLM queries tracked`} hint="Run checks anytime" cta="LLM Visibility" to="/llm-visibility" testid="outcome-llm" />
          )}
          {job.steps?.fix_kit?.status === "done" && (
            <OutcomeCard title="Auto-Fix Kit ready" hint={`${job.steps.fix_kit.patch_count ?? 0} paste-able patches`} cta="Open fix kit" to="/fix-kit" testid="outcome-kit" />
          )}
        </div>
      )}

      {failed && (
        <div className="mt-6 bg-red-950/30 border border-red-900/40 rounded-sm p-6" data-testid="autopilot-failed">
          <div className="flex items-center gap-2 text-red-400 mb-2"><AlertTriangle className="w-4 h-4" /> <span className="font-medium">Pipeline failed</span></div>
          <p className="text-sm text-red-300">{job.error || "Unknown error"}</p>
        </div>
      )}
    </DashboardLayout>
  );
}

function StepRow({ step, data, jobBlogCount }) {
  const status = data?.status || "pending";
  const Icon = step.icon;
  const map = {
    pending:  { color: "#52525B", bg: "bg-zinc-900",   pill: "Pending"  },
    running:  { color: "#0055FF", bg: "bg-[#0055FF]/10", pill: "Running" },
    done:     { color: "#10B981", bg: "bg-[#10B981]/10", pill: "Done"    },
    failed:   { color: "#EF4444", bg: "bg-[#EF4444]/10", pill: "Failed"  },
    skipped:  { color: "#A1A1AA", bg: "bg-zinc-900",   pill: "Skipped" },
  };
  const m = map[status] || map.pending;

  let detail = step.hint;
  if (step.key === "auto_blogs" && (status === "running" || status === "done")) {
    detail = `${data?.drafted ?? 0} of ${data?.target ?? jobBlogCount ?? 5} drafted`;
  } else if (step.key === "seo_audit" && status === "done") {
    detail = `Score: ${data?.score ?? "—"}/100`;
  } else if (step.key === "content_plan" && status === "done") {
    detail = `${data?.post_count ?? "—"} posts mapped`;
  } else if (step.key === "fix_kit" && status === "done") {
    detail = `${data?.patch_count ?? "—"} patches ready`;
  } else if (status === "failed" && data?.error) {
    detail = data.error;
  }

  return (
    <div className={`bg-zinc-950 border rounded-sm p-5 flex items-center gap-4 ${status === "running" ? "border-[#0055FF]/40" : "border-zinc-800"}`} data-testid={`step-${step.key}`}>
      <div className={`w-10 h-10 ${m.bg} border border-zinc-800 rounded-sm grid place-items-center flex-shrink-0`}>
        <Icon className="w-5 h-5" style={{ color: m.color }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-zinc-100">{step.label}</span>
          <Badge className="rounded-sm border" style={{ background: m.color + "15", color: m.color, borderColor: m.color + "40" }}>
            {m.pill}
          </Badge>
        </div>
        <p className="text-sm text-zinc-400 mt-1">{detail}</p>
      </div>
      {status === "running" && <Loader2 className="w-5 h-5 animate-spin text-[#0055FF]" />}
      {status === "done" && <CheckCircle2 className="w-5 h-5 text-[#10B981]" />}
      {status === "failed" && <XCircle className="w-5 h-5 text-[#EF4444]" />}
      {status === "pending" && <Clock className="w-5 h-5 text-zinc-700" />}
    </div>
  );
}

function OutcomeCard({ title, hint, cta, to, testid }) {
  return (
    <Link to={to} className="block bg-zinc-950 border border-zinc-800 rounded-sm p-5 hover:border-[#0055FF]/40 hover:-translate-y-0.5 transition" data-testid={testid}>
      <div className="font-display text-lg font-medium text-zinc-100">{title}</div>
      <p className="text-sm text-zinc-400 mt-1">{hint}</p>
      <div className="mt-4 text-xs text-[#3377FF] flex items-center gap-1">{cta} <ArrowRight className="w-3 h-3" /></div>
    </Link>
  );
}


// ============== GSC Money Loop: auto-generate blogs for underperforming GSC queries ==============
function GscMoneyLoop() {
  const [preview, setPreview] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const pollRef = useRef(null);

  const loadAll = async () => {
    try {
      const [{ data: sch }, { data: js }] = await Promise.all([
        api.get("/gsc-autopilot/schedule"),
        api.get("/gsc-autopilot/jobs?limit=5"),
      ]);
      setSchedule(sch);
      setJobs(js);
      try {
        const { data: prv } = await api.get("/gsc-autopilot/preview");
        setPreview(prv);
      } catch (e) {
        setPreview({ not_connected: true, message: e?.response?.data?.detail || "Connect Google in /integrations to see real picks." });
      }
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (!activeJob?.id) return;
    if (activeJob.status === "completed" || activeJob.status === "failed") return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/gsc-autopilot/jobs/${activeJob.id}`);
        setActiveJob(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          loadAll();
        }
      } catch {}
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeJob?.id, activeJob?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const runNow = async (dryRun = false) => {
    setRunning(true);
    try {
      const { data } = await api.post("/gsc-autopilot/run", { max_posts: schedule?.max_posts || 10, dry_run: dryRun });
      toast.success(dryRun ? "Dry-run queued — no blogs will be written." : "Money Loop started — we'll write & publish blogs for your underperforming queries.");
      const { data: full } = await api.get(`/gsc-autopilot/jobs/${data.job_id}`);
      setActiveJob(full);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  const saveSchedule = async (patch) => {
    setSavingSchedule(true);
    try {
      const merged = { ...schedule, ...patch };
      await api.post("/gsc-autopilot/schedule", merged);
      setSchedule(merged);
      toast.success("Schedule saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingSchedule(false);
    }
  };

  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const picksCount = preview?.picks?.length || 0;
  const notConnected = preview?.not_connected;

  return (
    <div className="mb-6 border border-[#10B981]/30 bg-gradient-to-br from-[#10B981]/8 via-zinc-950 to-zinc-950 rounded-sm overflow-hidden" data-testid="gsc-money-loop">
      <div className="px-6 py-5 border-b border-zinc-800 flex items-start gap-4 flex-wrap">
        <div className="w-11 h-11 rounded-sm bg-[#10B981]/15 border border-[#10B981]/40 grid place-items-center flex-shrink-0">
          <Zap className="w-5 h-5 text-[#10B981]" />
        </div>
        <div className="flex-1 min-w-[260px]">
          <h2 className="font-display text-lg font-semibold tracking-tight flex items-center gap-3">
            GSC Money Loop
            <Badge variant="outline" className="rounded-sm border-[#10B981]/40 text-[#10B981] text-[10px] tracking-widest uppercase">New</Badge>
          </h2>
          <p className="text-sm text-zinc-400 mt-1 leading-relaxed">
            Auto-publish blog posts targeting your <strong className="text-zinc-300">underperforming GSC queries</strong> (high impressions · position 5–20 · CTR &lt; 2%). Position 8 → 4 = real revenue.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!notConnected && (
            <Button onClick={() => runNow(true)} disabled={running || loading} variant="outline" className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm" data-testid="gsc-loop-dryrun">
              Dry run
            </Button>
          )}
          {!notConnected ? (
            <Button onClick={() => runNow(false)} disabled={running || loading || picksCount === 0} className="bg-[#10B981] hover:bg-[#10B981]/90 text-[#062D24] rounded-sm" data-testid="gsc-loop-run">
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
              Run now
            </Button>
          ) : (
            <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="gsc-loop-connect">
              <Link to="/integrations"><Plug className="w-4 h-4 mr-2" /> Connect Google</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="px-6 py-5 space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : notConnected ? (
          <div className="text-sm text-zinc-400 leading-relaxed">{preview?.message}</div>
        ) : (
          <>
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs tracking-widest uppercase text-zinc-500">Targets right now</h3>
                <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-500 text-[10px]">
                  {picksCount} / {preview?.total_queries_scanned || 0} queries
                </Badge>
              </div>
              {picksCount === 0 ? (
                <div className="text-sm text-zinc-500 border border-dashed border-zinc-800 rounded-sm p-4">
                  No underperforming queries right now — that's actually a good sign. The Money Loop scans for queries with impressions ≥ {preview?.thresholds?.min_impressions} · position {preview?.thresholds?.position_range?.[0]}–{preview?.thresholds?.position_range?.[1]} · CTR &lt; {preview?.thresholds?.ctr_max_pct}%.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="gsc-loop-picks">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                        <th className="pb-2 font-normal">Query</th>
                        <th className="pb-2 font-normal text-right">Impressions</th>
                        <th className="pb-2 font-normal text-right">Position</th>
                        <th className="pb-2 font-normal text-right">CTR</th>
                        <th className="pb-2 font-normal text-right">Opportunity</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.picks.slice(0, 10).map((p, i) => (
                        <tr key={i} className="border-b border-zinc-900 last:border-0">
                          <td className="py-2.5 max-w-xs truncate">{p.query}</td>
                          <td className="py-2.5 text-right text-zinc-300 font-mono">{p.impressions?.toLocaleString()}</td>
                          <td className="py-2.5 text-right text-zinc-400 font-mono">{p.position?.toFixed(1)}</td>
                          <td className="py-2.5 text-right text-amber-400 font-mono">{p.ctr?.toFixed(2)}%</td>
                          <td className="py-2.5 text-right">
                            <TrendingUp className="w-3.5 h-3.5 inline text-[#10B981]" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="border-t border-zinc-800 pt-5">
              <h3 className="text-xs tracking-widest uppercase text-zinc-500 mb-3">Weekly schedule</h3>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex items-center gap-3" data-testid="gsc-loop-toggle">
                  <Switch
                    checked={!!schedule?.enabled}
                    onCheckedChange={(v) => saveSchedule({ enabled: v })}
                    disabled={savingSchedule}
                  />
                  <span className="text-sm text-zinc-300">{schedule?.enabled ? "Enabled" : "Off"}</span>
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5 block">Day</Label>
                  <Select value={String(schedule?.day_of_week ?? 0)} onValueChange={(v) => saveSchedule({ day_of_week: parseInt(v) })}>
                    <SelectTrigger className="h-10 bg-zinc-950 border-zinc-800 rounded-sm w-28" data-testid="gsc-loop-day"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                      {dayNames.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5 block">Hour (UTC)</Label>
                  <Select value={String(schedule?.hour_utc ?? 9)} onValueChange={(v) => saveSchedule({ hour_utc: parseInt(v) })}>
                    <SelectTrigger className="h-10 bg-zinc-950 border-zinc-800 rounded-sm w-24" data-testid="gsc-loop-hour"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm max-h-60">
                      {Array.from({ length: 24 }, (_, i) => <SelectItem key={i} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5 block">Posts / run</Label>
                  <Select value={String(schedule?.max_posts ?? 10)} onValueChange={(v) => saveSchedule({ max_posts: parseInt(v) })}>
                    <SelectTrigger className="h-10 bg-zinc-950 border-zinc-800 rounded-sm w-20" data-testid="gsc-loop-count"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                      {[3, 5, 10, 15, 20].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {schedule?.next_run_at && schedule?.enabled && (
                  <div className="text-xs text-zinc-500 ml-auto">
                    <span className="text-zinc-600">Next run:</span> <span className="text-zinc-300">{new Date(schedule.next_run_at).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>

            {activeJob && (
              <div className="border-t border-zinc-800 pt-5">
                <h3 className="text-xs tracking-widest uppercase text-zinc-500 mb-3">Current run</h3>
                <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center gap-3">
                    {activeJob.status === "completed" ? <CheckCircle2 className="w-5 h-5 text-[#10B981]" /> :
                     activeJob.status === "failed" ? <XCircle className="w-5 h-5 text-red-400" /> :
                     <Loader2 className="w-5 h-5 animate-spin text-[#3377FF]" />}
                    <div className="flex-1">
                      <div className="text-sm text-zinc-200 capitalize">{activeJob.status} · {activeJob.step || "queued"}</div>
                      {activeJob.current_query && <div className="text-xs text-zinc-500 font-mono mt-0.5 truncate">"{activeJob.current_query}"</div>}
                      {activeJob.published_count != null && <div className="text-xs text-[#10B981] mt-0.5">{activeJob.published_count} blogs published</div>}
                      {activeJob.error && <div className="text-xs text-red-400 mt-0.5">{activeJob.error}</div>}
                    </div>
                    {activeJob.published_count > 0 && (
                      <Button asChild size="sm" variant="outline" className="bg-zinc-950 border-zinc-800 rounded-sm">
                        <Link to="/blog">View blogs <ArrowRight className="w-3 h-3 ml-1" /></Link>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {jobs.length > 0 && (
              <div className="border-t border-zinc-800 pt-5">
                <h3 className="text-xs tracking-widest uppercase text-zinc-500 mb-3">Recent runs</h3>
                <ul className="space-y-1.5" data-testid="gsc-loop-jobs">
                  {jobs.map(j => (
                    <li key={j.id} className="flex items-center gap-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
                      <span className="font-mono text-zinc-600">{new Date(j.created_at).toLocaleString()}</span>
                      <Badge variant="outline" className={`rounded-sm text-[10px] uppercase ${
                        j.status === "completed" ? "border-[#10B981]/40 text-[#10B981]" :
                        j.status === "failed" ? "border-red-500/40 text-red-400" :
                        "border-zinc-700 text-zinc-400"
                      }`}>{j.status}</Badge>
                      <span className="text-zinc-500">·</span>
                      <span>{j.published_count ?? 0} published</span>
                      {j.trigger === "schedule" && <Badge variant="outline" className="rounded-sm text-[10px] border-zinc-700 text-zinc-500">scheduled</Badge>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
